/**
 * Каталог РАЗДЕЛОВ для настраиваемых ролей и прав (RBAC).
 *
 * Единый источник правды для:
 *   - бэкенда (RbacService.resolveSections — пресет-карта по enum-ролям),
 *   - админки (Layout.tsx — гейтинг меню; экран «Роли и права» — матрица).
 *
 * Каждый раздел = один пункт меню. `presetRoles` повторяет roles из NAV_GROUPS
 * (источник — apps/admin Layout.tsx) — благодаря этому роль-без-кастома видит
 * РОВНО то же меню, что и сегодня (день-в-день паритет).
 *
 * Уровни доступа (лесенка, по возрастанию):
 *   HIDDEN  — раздела нет в меню
 *   VIEW    — видит, но кнопки создать/изменить/удалить скрыты
 *   CREATE  — VIEW + добавление новых записей
 *   FULL    — CREATE + редактирование/удаление/смена статусов
 */

import type { Role } from './index.js';

/** Роли desktop Admin, которым доступны исполнительские очереди производства. */
export const PRODUCTION_WORK_QUEUE_ROLES = ['ADMIN', 'PRODUCTION', 'INSTALLATION_MANAGER'] as const satisfies readonly Role[];

export type AccessLevel = 'HIDDEN' | 'VIEW' | 'CREATE' | 'FULL';

/** Уровни по возрастанию прав. Индекс = «сила». */
export const ACCESS_LEVELS: AccessLevel[] = ['HIDDEN', 'VIEW', 'CREATE', 'FULL'];

/** `a >= b` по лесенке доступа (FULL >= CREATE >= VIEW >= HIDDEN). */
export function levelGte(a: AccessLevel, b: AccessLevel): boolean {
  return ACCESS_LEVELS.indexOf(a) >= ACCESS_LEVELS.indexOf(b);
}

/** Минимальный уровень, при котором действие разрешено. */
export function levelAllows(level: AccessLevel, need: Exclude<AccessLevel, 'HIDDEN'>): boolean {
  return levelGte(level, need);
}

export interface SectionGroup {
  /** i18n-ключ заголовка группы (namespace nav). */
  key: string;
  titleKey: string;
}

export interface SectionDef {
  /** Стабильный ключ раздела (НЕ url). Используется в SectionPermission.section. */
  key: string;
  /** Группа меню. */
  group: string;
  /** Роут в админке (для предпросмотра меню). */
  route: string;
  /** i18n-ключ названия (namespace nav) — тот же, что у пункта меню. */
  labelKey: string;
  /** Роли, которые видят раздел СЕГОДНЯ (для пресет-карты). 'ALL' = все роли. */
  presetRoles: Role[] | 'ALL';
  /** Роли, которым базовый пресет даёт только просмотр этого раздела. */
  presetViewRoles?: Role[];
  /** Фича-флаг модуля: раздел существует только если модуль включён. */
  feature?: string;
  /** true = системный раздел: даже FULL в матрице не открывает его не-ADMIN
   *  (на бэке остаётся @Roles('ADMIN')-пол). UI это показывает и блокирует. */
  systemFloor?: boolean;
}

export const SECTION_GROUPS: SectionGroup[] = [
  { key: 'home',       titleKey: 'nav.sectionHome' },
  { key: 'sales',      titleKey: 'nav.sectionSales' },
  { key: 'production', titleKey: 'nav.sectionProduction' },
  { key: 'catalog',    titleKey: 'nav.sectionCatalog' },
  { key: 'warehouse',  titleKey: 'nav.sectionWarehouse' },
  { key: 'finance',    titleKey: 'nav.sectionFinance' },
  { key: 'analytics',  titleKey: 'nav.sectionAnalytics' },
  { key: 'fleet',      titleKey: 'nav.sectionFleet' },
  { key: 'marketing',  titleKey: 'nav.sectionMarketing' },
  { key: 'system',     titleKey: 'nav.sectionSystem' },
];

/** Полный каталог разделов. ДОЛЖЕН совпадать с HOME_NAV_ITEM + NAV_GROUPS в Layout.tsx. */
export const SECTIONS: SectionDef[] = [
  // ── Рабочий экран ──
  { key: 'dashboard', group: 'home', route: '/', labelKey: 'nav.dashboard', presetRoles: ['ADMIN', 'MANAGER', 'SERVICE_MANAGER', 'DEALER_MANAGER', 'FLEET_MANAGER', 'MARKETER', 'LOGIST', 'INSTALLATION_MANAGER', 'WAREHOUSE_MANAGER', 'PRODUCTION', 'ACCOUNTANT', 'SALES_HEAD'] },

  // ── Продажи ──
  { key: 'orders',          group: 'sales',      route: '/orders',              labelKey: 'nav.orders',          presetRoles: ['ADMIN', 'MANAGER', 'LOGIST', 'INSTALLATION_MANAGER', 'WAREHOUSE_MANAGER', 'PRODUCTION', 'ACCOUNTANT', 'SALES_HEAD'] },
  { key: 'measure_availability', group: 'sales', route: '/measure-availability', labelKey: 'nav.measureAvailability', presetRoles: ['ADMIN', 'MANAGER', 'LOGIST'] },
  { key: 'customers',       group: 'sales',      route: '/customers',           labelKey: 'nav.customers',       presetRoles: ['ADMIN', 'MANAGER', 'SERVICE_MANAGER', 'DEALER_MANAGER', 'ACCOUNTANT', 'SALES_HEAD'], presetViewRoles: ['SALES_HEAD'] },
  { key: 'service',         group: 'sales',      route: '/service',             labelKey: 'nav.service',         presetRoles: ['ADMIN', 'SERVICE_MANAGER'] },
  { key: 'contractors',     group: 'sales',      route: '/contractors',         labelKey: 'nav.contractors',     presetRoles: ['ADMIN'] },
  { key: 'dealers',         group: 'sales',      route: '/dealers',             labelKey: 'nav.dealers',         presetRoles: ['ADMIN', 'DEALER_MANAGER', 'ACCOUNTANT'] },
  { key: 'dealer_orders',   group: 'sales',      route: '/dealer-orders',       labelKey: 'nav.dealerOrders',    presetRoles: ['ADMIN', 'DEALER_MANAGER', 'PRODUCTION'] },

  // ── Производство и монтаж ──
  { key: 'measure_schedule',  group: 'production', route: '/measure-schedule',  labelKey: 'nav.measureSchedule', presetRoles: ['ADMIN', 'LOGIST'] },
  { key: 'install_schedule',  group: 'production', route: '/install-schedule',  labelKey: 'nav.installSchedule', presetRoles: ['ADMIN', 'INSTALLATION_MANAGER'] },
  { key: 'production_matrix', group: 'production', route: '/production-matrix', labelKey: 'nav.distribution',    presetRoles: ['ADMIN', 'INSTALLATION_MANAGER'] },
  { key: 'workshop_work',     group: 'production', route: '/workshop-work',     labelKey: 'productionWork.workshopTitle', presetRoles: [...PRODUCTION_WORK_QUEUE_ROLES], presetViewRoles: ['INSTALLATION_MANAGER'] },
  { key: 'contractor_work',   group: 'production', route: '/contractor-work',   labelKey: 'productionWork.contractorsTitle', presetRoles: [...PRODUCTION_WORK_QUEUE_ROLES], presetViewRoles: ['INSTALLATION_MANAGER'] },
  { key: 'inventory',         group: 'production', route: '/inventory',         labelKey: 'nav.inventory',      presetRoles: ['ADMIN', 'WAREHOUSE_MANAGER'], feature: 'features.inventory.enabled' },
  { key: 'cutting',           group: 'production', route: '/cutting',           labelKey: 'nav.cutting',         presetRoles: ['ADMIN', 'PRODUCTION'], feature: 'features.cutting.enabled' },
  { key: 'defects',           group: 'production', route: '/defects',           labelKey: 'nav.defects',         presetRoles: ['ADMIN', 'INSTALLATION_MANAGER'] },

  // ── Каталог ──
  { key: 'price_list',    group: 'catalog', route: '/price-list',    labelKey: 'nav.priceBook',    presetRoles: ['ADMIN'] },
  { key: 'price_bundles', group: 'catalog', route: '/price-bundles', labelKey: 'nav.priceBundles', presetRoles: ['ADMIN'] },
  { key: 'pricing',       group: 'catalog', route: '/pricing',       labelKey: 'nav.pricing',      presetRoles: ['ADMIN'] },

  // ── Склад ──
  { key: 'purchasing', group: 'warehouse', route: '/purchasing', labelKey: 'nav.purchasing', presetRoles: ['ADMIN', 'WAREHOUSE_MANAGER'], feature: 'features.purchasing.enabled' },

  // ── Финансы ──
  { key: 'team_reserves', group: 'finance', route: '/team-reserves', labelKey: 'nav.teamReserves', presetRoles: ['ADMIN', 'MANAGER', 'ACCOUNTANT', 'INSTALLATION_MANAGER', 'SALES_HEAD'], presetViewRoles: ['INSTALLATION_MANAGER', 'SALES_HEAD'] },
  { key: 'finance',              group: 'finance', route: '/finance',              labelKey: 'nav.finance',      presetRoles: ['ADMIN', 'ACCOUNTANT'] },
  { key: 'finance_sales_margin', group: 'finance', route: '/finance/sales-margin', labelKey: 'nav.salesMargin',  presetRoles: ['ADMIN', 'ACCOUNTANT', 'MANAGER', 'SALES_HEAD'], presetViewRoles: ['MANAGER', 'SALES_HEAD'] },
  { key: 'finance_receipts',     group: 'finance', route: '/finance/receipts',     labelKey: 'nav.receipts',     presetRoles: ['ADMIN', 'ACCOUNTANT'] },
  { key: 'payment_claims',       group: 'finance', route: '/payment-claims',       labelKey: 'nav.paymentClaims', presetRoles: ['ADMIN', 'ACCOUNTANT'] },
  { key: 'finance_cash_journal', group: 'finance', route: '/finance/cash-journal', labelKey: 'nav.cashJournal',  presetRoles: ['ADMIN', 'ACCOUNTANT'] },
  { key: 'expenses',             group: 'finance', route: '/expenses',             labelKey: 'nav.expenses',     presetRoles: ['ADMIN', 'ACCOUNTANT'], feature: 'features.expenses.enabled' },
  { key: 'daily_closes',         group: 'finance', route: '/daily-closes',         labelKey: 'nav.dailyCloses',  presetRoles: ['ADMIN', 'ACCOUNTANT'] },
  { key: 'installments',         group: 'finance', route: '/installments',         labelKey: 'nav.installments', presetRoles: ['ADMIN', 'ACCOUNTANT'] },

  // ── Аналитика ──
  { key: 'reports',        group: 'analytics', route: '/reports',        labelKey: 'nav.reports',       presetRoles: ['ADMIN', 'MANAGER', 'SERVICE_MANAGER', 'DEALER_MANAGER', 'ACCOUNTANT', 'LOGIST', 'INSTALLATION_MANAGER', 'FLEET_MANAGER', 'SALES_HEAD'], presetViewRoles: ['SALES_HEAD'] },
  { key: 'team_analytics', group: 'analytics', route: '/team-analytics', labelKey: 'nav.teamAnalytics', presetRoles: ['ADMIN', 'INSTALLATION_MANAGER', 'SALES_HEAD'], presetViewRoles: ['INSTALLATION_MANAGER', 'SALES_HEAD'] },
  { key: 'forecast',       group: 'analytics', route: '/forecast',       labelKey: 'nav.forecast',      presetRoles: ['ADMIN', 'MARKETER'] },
  { key: 'insights',       group: 'analytics', route: '/insights',       labelKey: 'nav.insights',      presetRoles: ['ADMIN', 'MARKETER'], feature: 'features.ai_insights.enabled' },
  { key: 'ai_assistant',   group: 'analytics', route: '/assistant',      labelKey: 'nav.assistant',     presetRoles: ['ADMIN', 'MARKETER'], feature: 'features.ai_assistant.enabled' },

  // ── Автопарк ──
  { key: 'fleet_map',         group: 'fleet', route: '/fleet/map',         labelKey: 'nav.fleetMap',         presetRoles: ['ADMIN', 'FLEET_MANAGER', 'LOGIST'] },
  { key: 'fleet_history',     group: 'fleet', route: '/fleet/history',     labelKey: 'nav.fleetHistory',     presetRoles: ['ADMIN', 'FLEET_MANAGER', 'LOGIST'] },
  { key: 'fleet_analytics',   group: 'fleet', route: '/fleet/analytics',   labelKey: 'nav.fleetAnalytics',   presetRoles: ['ADMIN', 'FLEET_MANAGER', 'LOGIST'], presetViewRoles: ['LOGIST'] },
  { key: 'fleet_calibration', group: 'fleet', route: '/fleet/calibration', labelKey: 'nav.fleetCalibration', presetRoles: ['ADMIN', 'FLEET_MANAGER'] },
  { key: 'fleet_zones',       group: 'fleet', route: '/fleet/zones',       labelKey: 'nav.fleetZones',       presetRoles: ['ADMIN', 'FLEET_MANAGER', 'LOGIST'], presetViewRoles: ['LOGIST'] },
  { key: 'fleet_devices',     group: 'fleet', route: '/fleet/devices',     labelKey: 'nav.fleetDevices',     presetRoles: ['ADMIN', 'FLEET_MANAGER', 'LOGIST'], presetViewRoles: ['LOGIST'] },

  // ── Маркетинг ──
  { key: 'reviews',   group: 'marketing', route: '/reviews',   labelKey: 'nav.reviews',   presetRoles: ['ADMIN', 'SERVICE_MANAGER', 'MARKETER'] },
  { key: 'referrals', group: 'marketing', route: '/referrals', labelKey: 'nav.referrals', presetRoles: ['ADMIN', 'MARKETER'] },

  // ── Система (только ADMIN: даже FULL в матрице не открывает её другим ролям) ──
  { key: 'users',         group: 'system', route: '/users',               labelKey: 'nav.users',         presetRoles: ['ADMIN'], systemFloor: true },
  { key: 'rbac',          group: 'system', route: '/roles',               labelKey: 'nav.rbac',          presetRoles: ['ADMIN'], systemFloor: true },
  { key: 'notifications', group: 'system', route: '/notifications',       labelKey: 'nav.notifications', presetRoles: ['ADMIN'], systemFloor: true },
  { key: 'audit',         group: 'system', route: '/audit',               labelKey: 'nav.audit',         presetRoles: ['ADMIN'], systemFloor: true },
  { key: 'amocrm',        group: 'system', route: '/integrations/amocrm', labelKey: 'nav.amocrm',        presetRoles: ['ADMIN'], systemFloor: true },
  { key: 'trash',         group: 'system', route: '/trash',               labelKey: 'nav.trash',         presetRoles: ['ADMIN'], systemFloor: true },
  { key: 'settings',      group: 'system', route: '/settings',            labelKey: 'nav.settings',      presetRoles: ['ADMIN'], systemFloor: true },
];

export const SECTION_KEYS: string[] = SECTIONS.map((s) => s.key);

const SECTION_BY_KEY = new Map(SECTIONS.map((s) => [s.key, s]));
export function sectionByKey(key: string): SectionDef | undefined {
  return SECTION_BY_KEY.get(key);
}

/** Map раздел→уровень для роли БЕЗ кастома (по enum-ролям).
 *  Обычно видимый раздел ⇒ FULL. «Заказы» — исключение: полный контроль остаётся
 *  у ADMIN/MANAGER, остальные системные роли получают только VIEW. */
export function presetSectionsForRoles(roles: Role[]): Record<string, AccessLevel> {
  const superUser = roles.includes('ADMIN');
  const managesOrders = roles.includes('MANAGER');
  const out: Record<string, AccessLevel> = {};
  for (const s of SECTIONS) {
    if (superUser) { out[s.key] = 'FULL'; continue; }
    const visible = s.presetRoles === 'ALL' || s.presetRoles.some((r) => roles.includes(r));
    const presetRoles = s.presetRoles;
    const matchedRoles = presetRoles === 'ALL' ? roles : roles.filter((role) => presetRoles.includes(role));
    const viewOnly = !!s.presetViewRoles?.length
      && matchedRoles.every((role) => s.presetViewRoles!.includes(role));
    out[s.key] = visible
      ? ((s.key === 'orders' && !managesOrders) || viewOnly ? 'VIEW' : 'FULL')
      : 'HIDDEN';
  }
  return out;
}

/** Карта прав, как её отдаёт /v1/rbac/me. */
export interface MySectionsResponse {
  hasCustomRole: boolean;
  customRole: { id: string; nameRu: string; nameUz: string } | null;
  /** section key → уровень. Отсутствующий ключ трактуется как HIDDEN. */
  sections: Record<string, AccessLevel>;
}

export interface CustomRoleDto {
  id: string;
  key: string;
  nameRu: string;
  nameUz: string;
  basePreset: Role | null;
  isActive: boolean;
  /** section key → уровень (только не-HIDDEN записи). */
  permissions: Record<string, AccessLevel>;
  /** Сколько сотрудников назначено на эту роль. */
  usersCount: number;
  createdAt: string;
}
